import type { AdminRouterContext } from '@/lib/router-context'
import type {
  NotificationRecipient as NotificationRecipientPayload,
  NotificationRecipientIdInput,
} from '@/lib/services/notification-recipients/notification-recipients.types'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import NotificationRecipientForm from '@/components/forms/NotificationRecipientForm'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { notificationEventQueryKeys } from '@/lib/services/notification-events/notification-events.query'
import {
  notificationRecipientQueries,
  notificationRecipientQueryKeys,
  useNotificationRecipient,
} from '@/lib/services/notification-recipients/notification-recipients.query'
import { notificationRecipientSchema } from '@/lib/services/notification-recipients/notification-recipients.schema'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { Bell, ChevronLeft } from 'lucide-react'
import { useEffect, useRef } from 'react'

type RecipientVerificationState =
  | Pick<NotificationRecipientPayload, 'id' | 'verified_at' | 'has_active_verification'>
  | Record<string, never>
  | null
  | undefined
export type NotificationRecipientLoaderInput = {
  context: {
    ability: Pick<AdminRouterContext['ability'], 'can'>
    queryClient: Pick<AdminRouterContext['queryClient'], 'ensureQueryData'>
  }
  params: { orgId: string | number; recipientId: NotificationRecipientIdInput }
}

const pendingVerificationRefetchInterval = 3_000

export function shouldPollPendingRecipientVerification(
  recipient: RecipientVerificationState
): boolean {
  return recipient?.verified_at == null && recipient?.has_active_verification === true
}

export function didRecipientFinishVerification(
  previousRecipient: RecipientVerificationState,
  nextRecipient: RecipientVerificationState
): boolean {
  return (
    previousRecipient?.id === nextRecipient?.id &&
    shouldPollPendingRecipientVerification(previousRecipient) &&
    nextRecipient?.verified_at != null
  )
}

export async function notificationRecipientLoader({
  context,
  params,
}: NotificationRecipientLoaderInput): Promise<{
  recipient: NotificationRecipientPayload
  isReadOnly: boolean
}> {
  if (!context.ability.can('view', 'notification-recipients')) throw notFound()

  const recipient = await context.queryClient.ensureQueryData(
    notificationRecipientQueries.show({
      organizationId: params.orgId,
      recipientId: params.recipientId,
    })
  )
  return { recipient, isReadOnly: !context.ability.can('edit', 'notification-recipients') }
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/notifications/$recipientId')({
  component: NotificationRecipient,
  head: adminRouteTitleHead,
  params: {
    parse: (params) => ({
      recipientId: notificationRecipientSchema.shape.recipientId.parse(params.recipientId),
    }),
  },
  loader: notificationRecipientLoader,
})

export function NotificationRecipient() {
  const { orgId, recipientId } = Route.useParams()
  const queryClient = useQueryClient()
  const path = usePathHelper()
  const previousRecipientRef = useRef<RecipientVerificationState>(null)
  const { data: recipient } = useNotificationRecipient(recipientId, {
    refetchInterval: (query) =>
      shouldPollPendingRecipientVerification(query.state.data)
        ? pendingVerificationRefetchInterval
        : false,
  })

  useEffect(() => {
    // Verification auto-subscribes on the API; refresh event mappings and recipient state when polling sees it finish.
    if (didRecipientFinishVerification(previousRecipientRef.current, recipient)) {
      void queryClient.invalidateQueries({
        queryKey: notificationEventQueryKeys.all({ organizationId: orgId }),
      })
      void queryClient.invalidateQueries({
        queryKey: notificationRecipientQueryKeys.all({ organizationId: orgId }),
      })
    }

    previousRecipientRef.current = recipient
  }, [orgId, queryClient, recipient])

  return (
    <div className="space-y-6 max-w-4xl">
      <AdminPageHeader
        icon={Bell}
        title="Recipient Details"
        actions={
          <AdminHeaderActionButton asChild variant="outline">
            <Link {...path.notifications.index().using({ orgId }).link}>
              <ChevronLeft />
              Back to Notifications
            </Link>
          </AdminHeaderActionButton>
        }
      />
      <NotificationRecipientForm recipient={recipient} />
    </div>
  )
}
